import JSZip from 'jszip';
import { MessageDescriptor } from 'react-intl';
import {
    MODULE_CUSTOM_HTML_ZIP_FILE_TYPE_NOT_ALLOWED_ERROR_TEXT,
    MODULE_CUSTOM_HTML_ZIP_NO_INDEX_HTML_ERROR_TEXT,
    MODULE_CUSTOM_HTML_ZIP_NOT_A_ZIP_ERROR_TEXT,
} from '../../../../../../event-hubs.text';

export type ZipContentError = {
    descriptor: MessageDescriptor;
    values?: Record<string, string>;
};

const INDEX_HTML = 'index.html';

// static web assets only, server-executable file types (php, jsp, sh, exe...) are not in the list
const ALLOWED_EXTENSIONS = [
    'html', 'htm', 'css', 'js', 'mjs', 'json', 'map',
    'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico', 'avif', 'bmp',
    'woff', 'woff2', 'ttf', 'otf', 'eot',
];

const MACOS_RESOURCE_FOLDER = '__MACOSX/';
const MACOS_RESOURCE_FILE_PREFIX = '._';
// macOS folder settings, Windows thumbnail caches and Windows folder settings, compared in lower case as the letter case of the names varies
const SYSTEM_FILE_NAMES = ['.ds_store', 'thumbs.db', 'ehthumbs.db', 'desktop.ini'];

const getFileName = (path: string): string => path.split('/').pop();

// files which macOS and Windows add to the archives and the folders they create, they are not a part of the content
const isSystemMetadata = (path: string): boolean => {
    const fileName = getFileName(path);
    return path.startsWith(MACOS_RESOURCE_FOLDER)
        || fileName.startsWith(MACOS_RESOURCE_FILE_PREFIX)
        || SYSTEM_FILE_NAMES.includes(fileName.toLowerCase());
};

// a name without a dot has no extension, a name starting with a dot (.htaccess) has the whole name as extension
const isAllowedFile = (path: string): boolean => {
    const fileName = getFileName(path);
    const extension = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
    return ALLOWED_EXTENSIONS.includes(extension);
};

export const validateCustomHtmlZipContent = async (file: File): Promise<ZipContentError | null> => {
    let zip: JSZip;
    try {
        zip = await JSZip.loadAsync(file);
    } catch (e) {
        return { descriptor: MODULE_CUSTOM_HTML_ZIP_NOT_A_ZIP_ERROR_TEXT };
    }

    // matches only a file in the root of the archive, a folder or a file inside of a folder is not found
    if (!zip.file(INDEX_HTML)) {
        return { descriptor: MODULE_CUSTOM_HTML_ZIP_NO_INDEX_HTML_ERROR_TEXT };
    }

    const notAllowedFiles: string[] = [];
    zip.forEach((path, entry) => {
        if (!entry.dir && !isSystemMetadata(path) && !isAllowedFile(path)) {
            notAllowedFiles.push(path);
        }
    });
    if (notAllowedFiles.length) {
        return { descriptor: MODULE_CUSTOM_HTML_ZIP_FILE_TYPE_NOT_ALLOWED_ERROR_TEXT, values: { fileName: notAllowedFiles[0] } };
    }

    return null;
};
